import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmailEditor from 'react-email-editor';
import styles from './HtmlTemplate.module.less';
import { HtmlTemplateView } from './HtmlTemplateView';
import { Button, Input } from 'antd';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';

const EmailEditorX: any = EmailEditor;

export type HtmlTemplate = {
  html: string;
  design: string;
  mergeTags?: string;
};
type Props = {
  value?: HtmlTemplate;
  onChange?: (value?: HtmlTemplate) => void;
  editorProps?: any;
  mergeTags?: boolean;
};

export const HtmlTemplateInput: FC<Props> = ({ editorProps, value, onChange, mergeTags }) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editorVisible, setEditorVisible] = useState<boolean>(false);
  const [editorReady, setEditorReady] = useState<boolean>(false); // NEW

  const designer = useRef<any>(null);
  const editorLoaded = useRef<boolean>(false);
  const designLoaded = useRef<boolean>(false);
  const editorId = useRef<string>(`template-editor-${Math.random().toString(36).slice(2, 11)}`);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!editing) {
      setEditorVisible(false);
      setEditorReady(false); // NEW
      editorLoaded.current = false;
      designLoaded.current = false;
      designer.current = null;
    }
  }, [editing]);

  const loadDesign = useCallback(() => {
    if (!designer.current || !editorLoaded.current || designLoaded.current) return;
    const currentValue = valueRef.current;
    if (currentValue?.design) {
      try {
        const parsed = JSON.parse(currentValue.design);
        designLoaded.current = true;
        designer.current.loadDesign(parsed);
      } catch (e) {
        console.error('Failed to parse design:', e);
      }
    } else {
      designLoaded.current = true;
    }
    // NO setEditorReady here anymore
  }, []);

  const onSave = useCallback(() => {
    if (!designer.current || !editorLoaded.current || !onChange) return;
    setSaving(true);
    designer.current.exportHtml((data: any) => {
      onChange({
        design: JSON.stringify(data.design),
        html: data.html,
        mergeTags: valueRef.current?.mergeTags,
      });
      setSaving(false);
      setEditing(false);
    });
  }, [onChange]);

  const mtgs = useMemo(() => {
    try {
      return mergeTags
        ? value?.mergeTags
          ? JSON.parse(value?.mergeTags)
          : undefined
        : editorProps?.options?.mergeTags;
    } catch (e) {
      return undefined;
    }
  }, [editorProps?.options?.mergeTags, mergeTags, value?.mergeTags]);

  return (
    <div className={styles.templateEditor}>
      {mergeTags && (
        <Input.TextArea
          value={value?.mergeTags}
          onChange={(e) =>
            onChange?.({ ...(value ?? { design: '', html: '' }), mergeTags: e.target.value })
          }
        />
      )}
      <div className={styles.templateEditorDisplay}>
        {value && <HtmlTemplateView value={value.html} />}
        <Button
          className={styles.templateEditorOverlay}
          type="ghost"
          onClick={() => setEditing(true)}>
          {' '}
        </Button>
      </div>
      <ResponsiveModal
        {...{
          afterOpenChange: (open: boolean) => {
            if (open) setEditorVisible(true);
          },
        }}
        title={'Template'}
        open={editing}
        onOk={onSave}
        onCancel={() => setEditing(false)}
        okText="Save"
        confirmLoading={saving}
        cancelText="Cancel"
        destroyOnClose={true}
        width={'96%'}
        closable={true}>
        {/* Loading spinner — visible until onLoad fires */}
        {!editorReady && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 300px)',
              gap: 12,
              color: '#888',
            }}>
            <div
              style={{
                width: 36,
                height: 36,
                border: '3px solid #f0f0f0',
                borderTop: '3px solid #1677ff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: 14 }}>Loading editor...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {editorVisible && (
          <EmailEditorX
            editorId={editorId.current}
            style={{
              width: '100%',
              minHeight: 'calc(100vh - 300px)',
              display: editorReady ? 'block' : 'none',
            }}
            ref={(instance: any) => {
              designer.current = instance;
              loadDesign();
            }}
            projectId={3657}
            onLoad={() => {
              editorLoaded.current = true;
              loadDesign();
            }}
            onReady={() => {
              // This fires when Unlayer has fully rendered its UI
              setEditorReady(true);
            }}
            {...editorProps}
            options={{
              ...editorProps?.options,
              mergeTags: mtgs,
            }}
          />
        )}
      </ResponsiveModal>
    </div>
  );
};
